// ─────────────────────────────────────────────────────────────
//  Reporting-Sync – hält Report ⇄ Rechnung ⇄ Team-Auszahlungen
//  konsistent. Alle drei hängen am selben Periodenschlüssel
//  (`slug__periodKey`):
//
//    • Reporting:   qualifizierteTermine  (Outreach-Manager)
//    • Rechnung:    extraAppointments     (Eingang, Betrag = Fix + extra×Preis)
//    • Auszahlung:  Termin-Vergütung      (Kosten)
//
//  Maßgeblich ist die Zahl der ABRECHENBAREN Extra-Termine:
//    extra      = max(0, qualifizierteTermine − included)
//    qualified  = extra + included
//  `included` kommt aus dem Vertrag (contract.includedAppointments) –
//  bei reinem Provisionsmodell 0, sonst der Standard von 4.
//
//  Die Termin-Vergütung des Teams kennt zwei Modi:
//    • Provisionsmodell aktiv + Mitglied hat eine Staffel
//        -> gestaffelte Provision auf die PROVISIONSBASIS des Vertrags
//           (nicht auf den Kundenpreis!), inkl. Unlocks & Minimum.
//    • sonst -> wie bisher: pauschaler Bonus je Extra-Termin.
//
//  buildExtraApptUpdates() liefert eine flache Multi-Path-Updates-Map
//  für ein einzelnes update(ref(database), updates) – atomar & idempotent.
// ─────────────────────────────────────────────────────────────
import {
  reportingId,
  paymentId,
  payoutId,
  includedAppointmentsFor,
  appointmentPriceFor,
  commissionForMember,
  customerRoleOverride,
} from './paymentEngine';

export const PAYMENTS_PATH = 'salesgod-research/payments';
export const PAYOUTS_PATH = 'salesgod-research/payouts';
export const REPORTINGS_PATH = 'salesgod-research/reportings';

// Baut die Updates, um die Extra-Termine einer Periode überall konsistent zu
// setzen. `extra` ist die maßgebliche Anzahl abrechenbarer Extra-Termine (>=0).
//
// ctx: { slug, periodKey, extra, qualified?, customers, team, payments, payouts, reportings, apptPrice }
//   apptPrice(slug) -> Netto-Preis je Termin aus dem Sales Research. Wird nur
//   genutzt, wenn weder die Rechnung noch der Vertrag einen Preis vorgibt.
//   qualified -> tatsächlich gemeldete Termine. Angeben, wann immer bekannt:
//   aus `extra` allein lässt sich die echte Zahl nicht rekonstruieren, sobald
//   WENIGER Termine als inkludiert stattgefunden haben (extra ist dann 0, aber
//   qualified kann 0,1,2,3 sein). Die Team-Provision hängt an dieser Zahl.
export function buildExtraApptUpdates(ctx) {
  const {
    slug, periodKey, extra, qualified: qualifiedIn,
    customers = {}, team = {}, payments = {}, payouts = {}, reportings = {},
    apptPrice,
  } = ctx || {};
  const updates = {};
  if (!slug || !periodKey) return updates;

  const cust = customers[slug] || {};
  const contract = cust.contract || null;
  const included = includedAppointmentsFor(contract);
  const ex = Math.max(0, Math.round(Number(extra)) || 0);
  const qualified = typeof qualifiedIn === 'number' && isFinite(qualifiedIn) && qualifiedIn >= 0
    ? Math.round(qualifiedIn)
    : ex + included;
  const ts = Date.now();

  // 1) Rechnung. Die Beträge sind nur editierbar, solange sie offen ist – eine
  //    gestellte oder bezahlte Rechnung wird nie nachträglich verändert.
  //    Die reine Terminzahl schreiben wir trotzdem immer mit: Sie ist keine
  //    Geldgröße, sondern die Bemessungsgrundlage der Team-Provision. Ohne sie
  //    ginge die Vergütung verloren, wenn das Reporting erst nach der
  //    Rechnungsstellung eintrifft.
  const pid = paymentId(slug, periodKey);
  const pay = payments[pid];
  if (pay) {
    const base = `${PAYMENTS_PATH}/${pid}`;
    updates[`${base}/appointments`] = qualified;
    updates[`${base}/updatedAt`] = ts;

    if (pay.status === 'due') {
      const curExtra = Number(pay.extraAppointments) || 0;
      const curPrice = Number(pay.appointmentPrice) || 0;
      const baseNet = typeof pay.baseNet === 'number'
        ? pay.baseNet
        : Math.max(0, (Number(pay.net) || 0) - curExtra * curPrice);
      // Bereits gestempelter Preis gewinnt (Rechnungshistorie bleibt stabil),
      // danach der intern festgelegte Vertragspreis, zuletzt der Research-Wert.
      const price = curPrice > 0
        ? curPrice
        : appointmentPriceFor(contract, typeof apptPrice === 'function' ? apptPrice(slug) : 0);
      const net = baseNet + ex * price;
      const vat = pay.taxMode === 'vat' ? net * (Number(pay.vatRate) || 0) / 100 : 0;
      updates[`${base}/baseNet`] = baseNet;
      updates[`${base}/appointmentPrice`] = price;
      updates[`${base}/extraAppointments`] = ex;
      updates[`${base}/net`] = net;
      updates[`${base}/vat`] = vat;
      updates[`${base}/amount`] = net + vat;
    }
  }

  // 2) Reporting (qualifizierte Termine + abgeleitete Extra-Termine).
  const rid = reportingId(slug, periodKey);
  if (reportings[rid]) {
    const base = `${REPORTINGS_PATH}/${rid}`;
    updates[`${base}/qualifiedAppointments`] = qualified;
    updates[`${base}/extraAppointments`] = ex;
    updates[`${base}/included`] = included;
    updates[`${base}/updatedAt`] = ts;
  }

  // 3) Team-Auszahlungen für alle zugewiesenen Mitglieder.
  const assigned = cust.team || {};
  Object.keys(assigned).forEach((memberId) => {
    const poId = payoutId(memberId, slug, periodKey);
    const po = payouts[poId];
    if (!po || po.status !== 'open') return; // bezahlte/stornierte Auszahlungen nicht anfassen
    const m = team[memberId] || {};
    const curExtra = Number(po.extraAppointments) || 0;
    const curBonus = Number(po.appointmentBonus) || 0;
    // Fixanteil/Retainer-Provision: alles, was nicht aus Terminen stammt.
    const baseAmount = typeof po.baseAmount === 'number'
      ? po.baseAmount
      : Math.max(0, (Number(po.amount) || 0) - curExtra * curBonus);
    const base = `${PAYOUTS_PATH}/${poId}`;

    const share = commissionForMember(m, contract, qualified, customerRoleOverride(cust, memberId));
    if (share) {
      updates[`${base}/commissionRole`] = share.roleId;
      updates[`${base}/commissionAmount`] = share.amount;
      updates[`${base}/commissionBase`] = share.commissionBase;
      updates[`${base}/commissionMinimumApplied`] = share.minimumApplied;
      updates[`${base}/appointmentBonus`] = 0;
      updates[`${base}/amount`] = baseAmount + share.amount;
    } else {
      const bonus = (typeof po.appointmentBonus === 'number' ? po.appointmentBonus : Number(m.appointmentBonus)) || 0;
      updates[`${base}/appointmentBonus`] = bonus;
      updates[`${base}/amount`] = baseAmount + ex * bonus;
    }
    updates[`${base}/baseAmount`] = baseAmount;
    updates[`${base}/extraAppointments`] = ex;
    updates[`${base}/appointments`] = qualified;
    updates[`${base}/updatedAt`] = ts;
  });

  return updates;
}
